import React, { useState, useEffect, useCallback } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { useQuery } from "@apollo/client";
import { LIST_MANUFACTURERS } from "apollo/queries/listManufacturers";
import { formatName, formatPhoneNumber } from "utils";
import { useAuthStore } from "stores/auth/authStore";
import SingleSelectField from "components/ui/single-select";
import { Input } from "components/ui/input";
import { ClientHomeIcon } from "components/Icons";
import { AutoCompleteInput } from "../Profile/AutoCompleteInput";

const Step4ShippingDestination = () => {
  const { register, control, watch, setValue, trigger, getValues, formState: { errors } } = useFormContext();
  const shippingDestination = watch("shippingDestination");
  const user = useAuthStore((state) => state.user);
  const { data: listManufacturersData } = useQuery(LIST_MANUFACTURERS);

  // State for autocomplete
  const [selectedPlace, setSelectedPlace] = useState({ name: null });
  const [, setPlaceAutocomplete] = useState(null);
  const [countryState, setCountryState] = useState("");
  const [countryCity, setCountryCity] = useState("");
  const [countryZipcode, setCountryZipcode] = useState("");
  const [aptOrSuite, setAptOrSuite] = useState("");
  const [, setIsDropdownActive] = useState(false);
  const [, setManualAddress] = useState("");

  const manufacturerOptions =
    listManufacturersData?.listManufacturers
      ?.filter((manufacturer) => !manufacturer.isTreeMaker)
      ?.map((manufacturer) => ({
        value: manufacturer.id,
        label: formatName(manufacturer.brand),
        name: manufacturer.brand,
      })) || [];

  const shippingOptions = [
    { label: "Ship to Me", value: "CUSTOMER" },
    { label: "Ship to Manufacturer", value: "MANUFACTURER" },
  ];

  useEffect(() => {
    if (shippingDestination === "CUSTOMER" && user) {
      const currentShippingAddress = getValues("shippingAddress") || {};

      // Pre-fill phone if empty
      if (user.phone && !currentShippingAddress.phone) {
        const formattedPhone = formatPhoneNumber(user.phone);
        setValue("shippingAddress.phone", formattedPhone, { shouldValidate: true });
      }

      const street = (user.streetNumber && user.streetName)
        ? `${user.streetNumber} ${user.streetName}`
        : (user.address || "");

      if (street && !currentShippingAddress.street) {
        setValue("shippingAddress.street", street, { shouldValidate: true });
        setManualAddress(street);
        setSelectedPlace({ name: street });
      }

      if (user.city && !currentShippingAddress.city) {
        setValue("shippingAddress.city", user.city, { shouldValidate: true });
        setCountryCity(user.city);
      }

      if (user.state && !currentShippingAddress.state) {
        setValue("shippingAddress.state", user.state, { shouldValidate: true });
        setCountryState(user.state);
      }

      if (user.zipCode && !currentShippingAddress.zipCode) {
        setValue("shippingAddress.zipCode", user.zipCode, { shouldValidate: true });
        setCountryZipcode(user.zipCode);
      }

      if (user.suiteOrApt && !currentShippingAddress.suiteOrApt) {
        setValue("shippingAddress.suiteOrApt", user.suiteOrApt, { shouldValidate: true });
        setAptOrSuite(user.suiteOrApt);
      }
    }
  }, [shippingDestination, user, setValue, getValues]);

  const handleShippingDestinationChange = (selectedOption) => {
    const val = selectedOption?.value || null;
    setValue("shippingDestination", val, { shouldValidate: true });
    if (val === "MANUFACTURER") {
      // Clear shipping address when shipping to manufacturer
      setValue("shippingAddress", null);
      setCountryState("");
      setCountryCity("");
      setCountryZipcode("");
      setAptOrSuite("");
      setManualAddress("");
      setSelectedPlace({ name: null });
    } else {
      // Clear manufacturer when shipping to customer
      setValue("manufacturerId", null);
      setValue("manufacturerName", null);
    }
  };

  const handlePhoneNumberChange = (event) => {
    const input = event.target;
    const rawInput = input.value;
    const cursorPosition = input.selectionStart;
    const rawValue = rawInput.replace(/\D/g, "");
    const value = rawValue.slice(0, 10);
    let formattedValue = "";

    if (value.length > 6) {
      formattedValue = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6)}`;
    } else if (value.length > 3) {
      formattedValue = `(${value.slice(0, 3)}) ${value.slice(3)}`;
    } else if (value.length > 0) {
      formattedValue = `(${value}`;
    }

    let numericCursorPosition = 0;
    for (let i = 0; i < cursorPosition; i++) {
      if (/\d/.test(rawInput[i])) {
        numericCursorPosition++;
      }
    }

    let adjustedCursorPosition = 0;
    let numericCount = 0;
    for (let i = 0; i < formattedValue.length; i++) {
      if (/\d/.test(formattedValue[i])) {
        numericCount++;
      }
      if (numericCount >= numericCursorPosition) {
        adjustedCursorPosition = i + 1;
        break;
      }
    }

    setTimeout(() => {
      input.value = formattedValue;
      input.selectionStart = input.selectionEnd = Math.min(
        adjustedCursorPosition,
        formattedValue.length
      );
    }, 0);

    setValue("shippingAddress.phone", formattedValue, { shouldValidate: true });
  };

  const handleManufacturerChange = useCallback((selectedOption) => {
    const val = selectedOption?.value || null;
    setValue("manufacturerId", val, { shouldValidate: true });
    setValue("manufacturerName", selectedOption?.name || null);
  }, [setValue]);

  // Memoized callbacks to prevent re-renders
  const handlePlaceSelect = useCallback((place) => {
    setSelectedPlace({ name: place.name });
    // Extract all address components and set nested form fields
    if (place.address_components) {
      let city = "", state = "", zipCode = "", subpremise = "";
      let streetNumber = "", route = "";

      place.address_components.forEach((component) => {
        const types = component.types;
        if (types.includes("locality")) {
          city = component.long_name;
        } else if (types.includes("administrative_area_level_1")) {
          state = component.short_name;
        } else if (types.includes("postal_code")) {
          zipCode = component.long_name;
        } else if (types.includes("subpremise")) {
          subpremise = component.long_name;
        } else if (types.includes("street_number")) {
          streetNumber = component.long_name;
        } else if (types.includes("route")) {
          route = component.long_name;
        }
      });

      const streetAddress = [streetNumber, route].filter(Boolean).join(" ");

      // Update local state
      setCountryState(state);
      setCountryCity(city);
      setCountryZipcode(zipCode);
      setAptOrSuite(subpremise);
      setManualAddress(streetAddress || place.formatted_address || place.name);

      // Set nested form fields
      setValue("shippingAddress.street", streetAddress || place.name, { shouldValidate: true });
      setValue("shippingAddress.city", city, { shouldValidate: true });
      setValue("shippingAddress.state", state, { shouldValidate: true });
      setValue("shippingAddress.zipCode", zipCode, { shouldValidate: true });
      setValue("shippingAddress.suiteOrApt", subpremise);

      // Trigger validation
      setTimeout(() => {
        trigger([
          "shippingAddress.street",
          "shippingAddress.city",
          "shippingAddress.state",
          "shippingAddress.zipCode",
        ]);
      }, 100);
    }
  }, [setValue, trigger]);

  const handleSetCountryState = useCallback((val) => {
    setCountryState(val);
    setValue("shippingAddress.state", val, { shouldValidate: true });
  }, [setValue]);

  const handleSetCountryCity = useCallback((val) => {
    setCountryCity(val);
    setValue("shippingAddress.city", val, { shouldValidate: true });
  }, [setValue]);

  const handleSetCountryZipcode = useCallback((val) => {
    setCountryZipcode(val);
    setValue("shippingAddress.zipCode", val, { shouldValidate: true });
  }, [setValue]);

  const handleSetAptOrSuite = useCallback((val) => {
    setAptOrSuite(val);
    setValue("shippingAddress.suiteOrApt", val);
  }, [setValue]);

  const handleSetManualAddress = useCallback((val) => {
    setManualAddress(val);
    // Update street field when manually typing (if not from autocomplete)
    if (val && !selectedPlace?.name) {
      setValue("shippingAddress.street", val, { shouldValidate: true });
    }
  }, [setValue, selectedPlace]);

  return (
    <div className="w-full">
      <h3 className="text-base sm:text-lg lg:text-xl font-bold font-montserrat text-[#2b364b] mb-4 sm:mb-6 text-center">
        Shipping Destination
      </h3>

      <div className="space-y-4 sm:space-y-6 max-w-2xl mx-auto">
        {/* Shipping Destination Selection */}
        <div>
          <Controller
            name="shippingDestination"
            control={control}
            rules={{ required: "Shipping destination is required" }}
            render={({ field, fieldState: { error } }) => (
              <>
                <SingleSelectField
                  label="Shipping Destination"
                  options={shippingOptions}
                  placeholder="Select shipping destination"
                  value={field.value}
                  onChange={(option) => {
                    field.onChange(option?.value || null);
                    handleShippingDestinationChange(option);
                    trigger("shippingDestination");
                  }}

                  className="w-full"
                  stylesOverride={{
                    dropdownIndicator: (base) => ({ ...base, marginBottom: "10px !important" }),
                    clearIndicator: (base) => ({ ...base, marginBottom: "10px !important" }),
                    placeholder: (base) => ({ ...base, paddingTop: "15px !important" }),
                    singleValue: (base) => ({ ...base, paddingTop: "15px !important" }),
                    control: (base) => ({
                      ...base,
                      borderColor: error ? "#ef4444" : "#999",
                      boxShadow: "none",
                      borderRadius: "9999px",
                      height: "51.8px",
                      cursor: "pointer",
                      "&:hover": { borderColor: error ? "#ef4444" : "#999" },
                      "&:focus-within": { borderColor: error ? "#ef4444" : "#999" },
                    }),
                  }}
                />
                {error && (
                  <p className="text-red-500 text-xs sm:text-sm mt-1 ml-3 font-montserrat">{error.message}</p>
                )}
              </>
            )}
          />
        </div>

        {/* Conditional Fields Based on Selection */}
        {shippingDestination === "CUSTOMER" && (
          <div className="space-y-3 sm:space-y-4 border-t pt-3 sm:pt-4">
            <h4 className="text-sm sm:text-base lg:text-lg font-semibold text-[#2b364b] mb-3 sm:mb-4 font-montserrat">
              Shipping Address
            </h4>

            {/* Address Autocomplete */}
            <div className="w-full">
              <input
                type="hidden"
                {...register("shippingAddress.street", {
                  required: "Street address is required",
                })}
              />
              <AutoCompleteInput
                searchType="address"
                onPlaceSelect={handlePlaceSelect}
                setPlaceAutocomplete={setPlaceAutocomplete}
                setCountryState={handleSetCountryState}
                setCountryCity={handleSetCountryCity}
                setCountryZipcode={handleSetCountryZipcode}
                selectedPlace={selectedPlace}
                setSelectedPlace={setSelectedPlace}
                setAptOrSuite={handleSetAptOrSuite}
                onDropdownStateChange={setIsDropdownActive}
                setManualAddress={handleSetManualAddress}
                hasError={!!errors?.shippingAddress?.street}
              />
              {errors?.shippingAddress?.street && (
                <p className="text-red-500 text-xs sm:text-sm mt-1 ml-3 font-montserrat">{errors.shippingAddress.street.message}</p>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Input
                  {...register("shippingAddress.city", {
                    required: "City is required",
                  })}
                  type="text"
                  label="City *"
                  placeholder="Enter city"
                  icon={<ClientHomeIcon className="!text-transparent" />}
                  className={`w-full ${errors?.shippingAddress?.city ? "!border-red-500" : ""}`}
                  value={countryCity}
                  onChange={(e) => {
                    setValue("shippingAddress.city", e.target.value, { shouldValidate: true });
                    setCountryCity(e.target.value);
                  }}
                />
                {errors?.shippingAddress?.city && (
                  <p className="text-red-500 text-xs sm:text-sm mt-1 ml-3 font-montserrat">{errors.shippingAddress.city.message}</p>
                )}
              </div>

              <div>
                <Input
                  {...register("shippingAddress.state", {
                    required: "State is required",
                  })}
                  type="text"
                  label="State *"
                  placeholder="Enter state"
                  icon={<ClientHomeIcon className="!text-transparent" />}
                  className={`w-full ${errors?.shippingAddress?.state ? "!border-red-500" : ""}`}
                  value={countryState}
                  onChange={(e) => {
                    setValue("shippingAddress.state", e.target.value, { shouldValidate: true });
                    setCountryState(e.target.value);
                  }}
                />
                {errors?.shippingAddress?.state && (
                  <p className="text-red-500 text-xs sm:text-sm mt-1 ml-3 font-montserrat">{errors.shippingAddress.state.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Input
                  {...register("shippingAddress.zipCode", {
                    required: "Zip code is required",
                  })}
                  type="text"
                  label="Zip Code *"
                  placeholder="Enter zip code"
                  icon={<ClientHomeIcon className="!text-transparent" />}
                  className={`w-full ${errors?.shippingAddress?.zipCode ? "!border-red-500" : ""}`}
                  value={countryZipcode}
                  onChange={(e) => {
                    setValue("shippingAddress.zipCode", e.target.value, { shouldValidate: true });
                    setCountryZipcode(e.target.value);
                  }}
                />
                {errors?.shippingAddress?.zipCode && (
                  <p className="text-red-500 text-xs sm:text-sm mt-1 ml-3 font-montserrat">{errors.shippingAddress.zipCode.message}</p>
                )}
              </div>

              <div>
                <Input
                  {...register("shippingAddress.suiteOrApt")}
                  type="text"
                  label="Suite/APT"
                  placeholder="Optional"
                  icon={<ClientHomeIcon className="!text-transparent" />}
                  className="w-full"
                  value={aptOrSuite}
                  onChange={(e) => {
                    setValue("shippingAddress.suiteOrApt", e.target.value, { shouldValidate: true });
                    setAptOrSuite(e.target.value);
                  }}
                />
              </div>
            </div>

            <div>
              <Input
                {...register("shippingAddress.phone", {
                  required: "Phone number is required",
                })}
                type="text"
                label="Phone Number *"
                placeholder="(555) 123-4567"
                icon={<ClientHomeIcon className="!text-transparent" />}
                className={`w-full ${errors?.shippingAddress?.phone ? "!border-red-500" : ""}`}
                onChange={handlePhoneNumberChange}
              />
              {errors?.shippingAddress?.phone && (
                <p className="text-red-500 text-xs sm:text-sm mt-1 ml-3 font-montserrat">{errors.shippingAddress.phone.message}</p>
              )}
            </div>

            <Input
              {...register("shippingAddress.country")}
              type="text"
              label="Country"
              value="USA"
              readOnly
              disabled
              icon={<ClientHomeIcon className="!text-transparent" />}
              className="w-full"
            />
          </div>
        )}

        {shippingDestination === "MANUFACTURER" && (
          <div className="space-y-3 sm:space-y-4 border-t pt-3 sm:pt-4">
            <h4 className="text-sm sm:text-base lg:text-lg font-semibold font-montserrat text-[#2b364b] mb-3 sm:mb-4">
              Select Manufacturer
            </h4>

            <Controller
              name="manufacturerId"
              control={control}
              rules={{
                required:
                  shippingDestination === "MANUFACTURER"
                    ? "Manufacturer is required"
                    : false,
              }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <SingleSelectField
                    label="Manufacturer"
                    options={manufacturerOptions}
                    placeholder="Select manufacturer"
                    value={field.value}
                    onChange={(option) => {
                      field.onChange(option?.value || null);
                      handleManufacturerChange(option);
                      trigger("manufacturerId");
                    }}
                    className="w-full"
                    stylesOverride={{
                      dropdownIndicator: (base) => ({ ...base, marginBottom: "10px !important" }),
                      clearIndicator: (base) => ({ ...base, marginBottom: "10px !important" }),
                      placeholder: (base) => ({ ...base, paddingTop: "15px !important" }),
                      singleValue: (base) => ({ ...base, paddingTop: "15px !important" }),
                      control: (base) => ({
                        ...base,
                        borderColor: error ? "#ef4444" : "#999",
                        boxShadow: "none",
                        borderRadius: "9999px",
                        height: "51.8px",
                        cursor: "pointer",
                        "&:hover": { borderColor: error ? "#ef4444" : "#999" },
                        "&:focus-within": { borderColor: error ? "#ef4444" : "#999" },
                      }),
                    }}
                  />
                  {error && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1 ml-3 font-montserrat">{error.message}</p>
                  )}
                </>
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Step4ShippingDestination;
